import { useSession, useUser } from '@descope/react-sdk'
import type {
  FilterChangedEvent,
  GridReadyEvent,
  ITooltipParams,
  ModelUpdatedEvent,
} from 'ag-grid-community'
import {
  type ColDef,
  type ColumnState,
  type GridApi,
  type GridOptions,
  type IRowNode,
  ModuleRegistry,
  type RowSelectionOptions,
  TooltipModule,
} from 'ag-grid-community'
import _ from 'lodash'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useOrgIdFromToken } from '@/lib/auth/orgid.hook'
import {
  LocalStorageKeys,
  composeTableStorageKey,
  readStoredChips,
  useScopedLocalStorage,
  writeStoredChips,
} from '@/lib/storage/local-storage.hook'
import { cn } from '@/lib/ui/style-helpers'
import { useBaseUrlParams } from '@/lib/url/base-url-param.hook'

import type {
  EmptyStateButton,
  TableEmptyStateDefinition,
} from '@/components/lemmala-design-system/table/types'

import { AgTable, type AgTableProps } from './ag-table'
import { AgDataTableWrapper } from './components/ag-data-table-wrapper'
import { AgFilterBar } from './components/ag-filter-bar'
import { AgMetricCards } from './components/ag-metric-cards'
import { AgTableEmptyState } from './components/ag-table-empty-state'
import { AgViewsSwitcher } from './components/ag-views-switcher'
import { RowHoverTooltip } from './components/row-hover-tooltip'
import {
  TABLE_FILTER_BAR_HEIGHT,
  TABLE_HEADER_HEIGHTS,
  TABLE_TOOLTIP_SHOW_DELAY_MS,
} from './consts'
import { AgGridProvider } from './contexts/ag-grid-provider'
import { CsvExportConfig } from './hooks/use-ag-csv-export'
import { useAgUrlSync } from './hooks/use-ag-url-sync'
import { useAgViewCounters } from './hooks/use-ag-view-counters'
import { useAgViewManager } from './hooks/use-ag-view-manager'
import { useFilterModelEvaluator } from './hooks/use-filter-model-evaluator'
import { useHugTableContext } from './hooks/use-hug-table-context'
import type {
  AgDataTableTrackingContext,
  FilterModel,
  MetricCardItem,
  MetricCardsConfig,
  RowClickMeta,
  RowGroupingConfig,
  TableMode,
} from './types'
import { createAgGridTheme, getDefaultColDef, mergeGridOptions } from './utils/grid-theme'
import { safeStringify } from './utils/safe-stringify'

ModuleRegistry.registerModules([TooltipModule])

const EMPTY_URL_PARAM_KEYS = {}

export type AgDataTableProps<TRow extends Record<string, unknown>> = {
  // Required
  rowData: TRow[]
  columnDefs: ColDef<TRow>[]

  // Views (optional)
  views?: {
    options: string[]
    getDefinition: (name: string) => {
      label?: string
      columnState: ColumnState[]
      filterModel?: FilterModel
      filterBarFilters?: string[]
    }
    defaultView?: string
    icons?: Record<string, React.ReactNode>
    defaultCounters?: Record<string, number>
    perViewFilters?: boolean
  }

  // Column customization (optional)
  columnCustomization?: {
    enabled?: boolean
    /** localStorage key for persisting column state */
    storageKey?: LocalStorageKeys
    /** Tenant ID for scoping column state per tenant */
    tenantId?: string
  }

  // URL sync (enabled by default)
  urlSync?: {
    enabled?: boolean
    prefix?: string
    applyQuickFilter?: boolean
    paramKeys?: { search?: string; view?: string; filter?: string; sort?: string; columns?: string }
    serializers?: {
      getFilterUrlUpdates?: (m: FilterModel) => {
        update: Array<[string, string]>
        remove: string[]
      }
      urlToFilterModel?: (p: Record<string, string | undefined>) => FilterModel
      sortModelToUrlParam?: (m: Array<{ colId: string; sort: 'asc' | 'desc' }>) => string | null
      urlParamToSortModel?: (v: string) => Array<{ colId: string; sort: 'asc' | 'desc' }>
      columnStateToUrlParam?: (columnState: ColumnState[]) => string | null
      urlParamToColumnState?: (
        urlParam: string | null | undefined,
        allColumnIds: string[],
      ) => Partial<ColumnState>[] | null
    }
  }

  // External search only
  search?: {
    minQueryLength?: number
    value?: string
    onChange?: (text: string) => void
    // Generic row matcher; if omitted, a conservative stringified row includes check is used
    doesRowMatch?: (row: TRow, query: string) => boolean
    // Optional results payload for downstream highlighters (passed through to SearchContext)
    results?: unknown[]
    placeholder?: string
    containerClassName?: string
  }

  // Filters (toolbar chips)
  filters?: {
    // Provide set values per field (optional). If omitted, rely on column filter params/defaults
    getValuesForField?: (field: string, rows: TRow[]) => string[] | undefined
    // Default visible filters (always shown and non-removable)
    defaultVisibleFilters?: string[]
    // Filters that can be removed by the user
    removableFilters?: string[]
  }
  // Important to allow filter popovers to catch interactions in modals
  inModal?: boolean

  // Selection & export
  selection?: {
    mode?: 'none' | 'singleRow' | 'multiple'
    checkboxes?: boolean
    selectOnClick?: boolean
    /** Return false to make a row un-selectable, e.g. a synthetic group header. */
    isRowSelectable?: (node: IRowNode<TRow>) => boolean
    /** Hide, rather than grey out, the checkbox on un-selectable rows. */
    hideDisabledCheckboxes?: boolean
  }
  exportCsv?: { enabled?: boolean }

  // Show/hide switches
  hideViewsSwitcher?: boolean
  hideFilters?: boolean
  hideSearch?: boolean
  hideExport?: boolean
  hideCount?: boolean
  hideHeader?: boolean

  // Styling overrides
  toolbarClassName?: string

  // Layout slots
  headerLeft?: React.ReactNode
  headerRight?: React.ReactNode
  footer?: React.ReactNode
  customFilterBarLeft?: React.ReactNode
  /**
   * Quick-filter cards between the views switcher and the filter bar. Clicking one filters the
   * configured column, so the filter bar shows a chip the user can see and clear.
   */
  metricCards?: MetricCardsConfig

  // Grid
  gridOptions?: GridOptions<TRow>
  defaultColDef?: ColDef<TRow>
  withFillerColumn?: boolean
  borderless?: boolean
  domLayout?: AgTableProps['domLayout']
  emptyStateContent?: AgTableProps['emptyStateContent']

  // Events
  onRowClick?: (row: TRow, meta: RowClickMeta) => void
  onGridInit?: (event: GridReadyEvent<TRow>) => void
  onGridReady?: (api: GridApi<TRow>) => void
  onViewChange?: (name: string) => void
  onFilterChanged?: (params: FilterChangedEvent<TRow>) => void
  onSearchChange?: (text: string) => void

  // Tooltips
  getRowTooltip?: (row: TRow) => React.ReactNode | undefined
  /** Allow pointer interaction with the tooltip content. */
  rowTooltipInteractive?: boolean
  /** Follow the cursor instead of anchoring the tooltip to the hovered cell. */
  rowTooltipMouseTrack?: boolean

  // Styling
  className?: string
  agCsvExportConfig?: CsvExportConfig
  preventFilters?: boolean

  // Analytics
  trackingContext?: AgDataTableTrackingContext

  // Table Classname
  tableClassName?: string

  // Empty state customization
  entityName?: string
  headerEmptyStateMessage?: string
  subHeaderEmptyStateMessage?: string
  emptyStateButton?: EmptyStateButton
  emptyStateDefinition?: TableEmptyStateDefinition

  // Dynamic height (wraps only the table, not the filter bar)
  containerClassName?: string
  grouping?: RowGroupingConfig<TRow>
  searchPlacement?: 'left' | 'right'
}

export const AgDataTable = <TRow extends Record<string, unknown> = Record<string, unknown>>({
  rowData,
  columnDefs,
  views,
  columnCustomization,
  urlSync,
  search,
  filters,
  selection,
  exportCsv,
  hideViewsSwitcher = false,
  hideSearch = false,
  hideExport = false,
  hideCount = false,
  hideHeader = false,
  hideFilters = false,
  toolbarClassName,
  headerLeft,
  headerRight,
  footer,
  customFilterBarLeft,
  metricCards,
  gridOptions,
  defaultColDef,
  withFillerColumn,
  borderless = false,
  onRowClick,
  onGridInit,
  onGridReady,
  onViewChange,
  onSearchChange,
  getRowTooltip,
  rowTooltipInteractive = false,
  rowTooltipMouseTrack = false,
  className,
  tableClassName,
  domLayout = 'normal',
  emptyStateContent,
  onFilterChanged,
  agCsvExportConfig,
  preventFilters = false,
  trackingContext,
  inModal,
  entityName,
  headerEmptyStateMessage,
  subHeaderEmptyStateMessage,
  emptyStateButton,
  emptyStateDefinition,
  containerClassName,
  grouping,
  searchPlacement,
}: AgDataTableProps<TRow>) => {
  const { user } = useUser()
  const { sessionToken } = useSession()
  const orgDetails = useOrgIdFromToken(user, sessionToken)
  // Filter persistence (chips + values) requires a signed-in user/tenant and a
  // unique table prefix; undefined disables it (e.g. drawers with preventFilters).
  const persistFilters = !preventFilters && (urlSync?.enabled ?? true) && !!urlSync?.prefix
  const filterStorageKey = persistFilters
    ? composeTableStorageKey(urlSync?.prefix, user?.userId, orgDetails?.orgId)
    : undefined
  const { isInHugContainer, availableHeight } = useHugTableContext()
  // Drive the hug check off ag-grid's actual rendered row heights, not rowData.length
  // or a guessed per-row constant, so filters/search and variable row heights
  // (e.g. group rows vs data rows) all toggle the hug correctly.
  const [displayedContentHeight, setDisplayedContentHeight] = useState(0)
  // The cards eat into the room the rows have, so the hug check has to count them too.
  const metricCardsRef = useRef<HTMLDivElement>(null)
  const [metricCardsHeight, setMetricCardsHeight] = useState(0)
  const contentFits =
    isInHugContainer &&
    availableHeight !== null &&
    displayedContentHeight > 0 &&
    displayedContentHeight +
      TABLE_HEADER_HEIGHTS.main +
      TABLE_FILTER_BAR_HEIGHT +
      metricCardsHeight <=
      availableHeight
  const tableMode: TableMode = contentFits ? 'hug' : domLayout === 'print' ? 'print' : 'full'

  const [gridApi, setGridApi] = useState<GridApi<TRow> | null>(null)
  const [filterModel, setFilterModel] = useState<FilterModel>({})

  // External search state (uncontrolled by default)
  const [internalSearch, setInternalSearch] = useState<string>('')
  const searchText = (search?.value ?? internalSearch) || ''
  const minQueryLength = search?.minQueryLength ?? 2

  const handleSearchChange = useCallback(
    (text: string) => {
      search?.onChange?.(text)
      onSearchChange?.(text)
      if (search?.value === undefined) setInternalSearch(text)
    },
    [search, onSearchChange],
  )

  // Use localStorage hook for column state persistence (tenant-scoped when tenantId is provided)
  const [storedColumnState, setStoredColumnState, clearStoredColumnState] = useScopedLocalStorage(
    columnCustomization?.storageKey ?? LocalStorageKeys.agGridColumnsInventory,
    columnCustomization?.tenantId ?? null,
  )

  // Extract all column IDs for column customization (must be defined before view manager)
  const allColumnIds = useMemo(() => {
    if (!columnCustomization?.enabled) return []
    return columnDefs
      .filter((col) => !col.suppressColumnsToolPanel)
      .map((col) => col.colId || col.field || '')
      .filter(Boolean) as string[]
  }, [columnDefs, columnCustomization?.enabled])

  const getStoredColumnState = useCallback(
    () => (columnCustomization?.enabled ? (storedColumnState as ColumnState[] | null) : null),
    [columnCustomization?.enabled, storedColumnState],
  )

  // Views
  const { urlParams, replaceParams } = useBaseUrlParams()
  // Apply prefix to paramKeys for view manager (same logic as useAgUrlSync)
  const prefixedParamKeys = useMemo(() => {
    if (!urlSync?.prefix) return urlSync?.paramKeys ?? {}
    const prefix = urlSync.prefix
    const baseKeys = urlSync?.paramKeys ?? {}
    return {
      view: prefix ? `${prefix}_${baseKeys.view || 'VIEW'}` : baseKeys.view || 'VIEW',
      filter: prefix ? `${prefix}_${baseKeys.filter || 'FILTER'}` : baseKeys.filter || 'FILTER',
      sort: prefix ? `${prefix}_${baseKeys.sort || 'SORT'}` : baseKeys.sort || 'SORT',
      columns: prefix
        ? `${prefix}_${baseKeys.columns || 'COLUMNS'}`
        : baseKeys.columns || 'COLUMNS',
    }
  }, [urlSync])

  // Chip (visible filter) persistence shares the same storage entry as filter values.
  const chipStorage = useMemo(() => {
    if (!filterStorageKey) return undefined
    return {
      read: () => readStoredChips(filterStorageKey),
      write: (chips: string[]) => writeStoredChips(filterStorageKey, chips),
    }
  }, [filterStorageKey])

  const { currentView, applyView, isApplyingViewRef } = useAgViewManager<TRow>(gridApi, {
    getViewDefinition: (name) => views?.getDefinition(name) || { columnState: [], filterModel: {} },
    viewOptions: views?.options ?? [],
    urlManager: {
      urlParams: preventFilters ? {} : (urlParams as Record<string, string>),
      replaceParams,
    },
    defaultView: views?.defaultView || 'All',
    allColumnIds,
    urlParamKeys: prefixedParamKeys,
    urlSerializers: {
      urlParamToColumnState: urlSync?.serializers?.urlParamToColumnState,
      urlToFilterModel: urlSync?.serializers?.urlToFilterModel,
    },
    onViewChange,
    perViewFilters: views?.perViewFilters,
    getStoredColumnState,
    filterStorageKey,
  })

  // URL sync (normalize nulls to undefined to satisfy typing)
  const definedUrlParams = useMemo(() => {
    const entries = Object.entries(urlParams ?? {}) as Array<[string, string | null | undefined]>
    const normalized = Object.fromEntries(entries.map(([k, v]) => [k, v ?? undefined])) as Record<
      string,
      string | undefined
    >
    return normalized
  }, [urlParams])

  // Merge localStorage column state into column definitions
  const columnDefsWithStoredState = useMemo(() => {
    // Skip if column customization not enabled or no column IDs or no stored state
    if (
      !columnCustomization?.enabled ||
      !allColumnIds.length ||
      !storedColumnState ||
      !Array.isArray(storedColumnState)
    ) {
      return columnDefs
    }

    try {
      const fixedColumnDefs = columnDefs.filter((colDef) => colDef.suppressColumnsToolPanel)
      const customizableColumnDefs = columnDefs.filter((colDef) => !colDef.suppressColumnsToolPanel)

      // Create a map of customizable colId to column def for quick lookup
      const colDefMap = new Map(
        customizableColumnDefs.map((colDef) => [colDef.colId || colDef.field, colDef]),
      )

      // Reorder and merge: use stored state order, then append any columns not in stored state
      const reorderedDefs: ColDef<TRow>[] = []

      // Type assertion: we know it's ColumnState[] because of the Array.isArray check above
      const columnStates = storedColumnState as ColumnState[]

      // First, add columns in the order specified by stored state
      for (const state of columnStates) {
        if (!allColumnIds.includes(state.colId)) continue

        const colDef = colDefMap.get(state.colId)
        if (colDef) {
          reorderedDefs.push({
            ...colDef,
            hide: state.hide ?? false,
          })
          colDefMap.delete(state.colId) // Remove from map to avoid duplicates
        }
      }

      // Then append any remaining columns that weren't in the stored state
      // (this handles cases where new columns are added to the grid)
      for (const colDef of colDefMap.values()) {
        reorderedDefs.push(colDef)
      }

      return [...fixedColumnDefs, ...reorderedDefs]
    } catch {
      return columnDefs
    }
  }, [columnDefs, columnCustomization?.enabled, allColumnIds, storedColumnState])

  // Build view items for switcher (label/icon/counter computed via hooks)
  const evaluate = useFilterModelEvaluator<TRow>(columnDefsWithStoredState)
  const counters = useAgViewCounters<TRow>(
    rowData,
    views?.options ?? [],
    (n) => (views ? views.getDefinition(n) : { columnState: [], filterModel: {} }),
    (r, m) => evaluate(r, m),
    views?.defaultCounters,
  )
  const viewItems = useMemo(() => {
    if (!views?.options?.length)
      return [] as Array<{
        name: string
        label?: React.ReactNode
        icon?: React.ReactNode
        counter?: number
      }>
    return views.options.map((name) => {
      const def = views.getDefinition(name) as { label?: string; icon?: React.ReactNode }
      return {
        name,
        label: def?.label ?? name,
        icon: def?.icon,
        counter: counters[name] ?? 0,
      }
    })
  }, [views, counters])

  useAgUrlSync<TRow>(gridApi, {
    enabled: urlSync?.enabled ?? true,
    prefix: urlSync?.prefix,
    urlManager: { urlParams: preventFilters ? {} : definedUrlParams, replaceParams },
    urlSerializers: urlSync?.serializers,
    urlParamKeys: urlSync?.paramKeys ?? EMPTY_URL_PARAM_KEYS,
    viewState: { currentView: currentView || views?.defaultView || 'All', isApplyingViewRef },
    searchState: { quickFilterText: searchText, onSearchChange: handleSearchChange },
    applyQuickFilter: urlSync?.applyQuickFilter ?? false,
    defaultView: views?.defaultView || 'All',
    filterStorageKey,
    allColumnIds,
  })

  // External filter (search)
  const isExternalFilterPresent = useCallback(() => {
    const trimmed = searchText.trim()
    return trimmed.length >= minQueryLength
  }, [searchText, minQueryLength])

  const doesExternalFilterPass = useCallback(
    (node: IRowNode<TRow>) => {
      const data = node?.data
      if (!data) return true
      const trimmed = searchText.trim()
      if (trimmed.length < minQueryLength) return true
      if (search?.doesRowMatch) return search.doesRowMatch(data, trimmed)
      // Fallback: naive stringify match (BigInt-safe)
      const haystack = safeStringify(data)
      return haystack.toLowerCase().includes(trimmed.toLowerCase())
    },
    [searchText, minQueryLength, search],
  )

  // Selection defaults
  const rowSelection = useMemo<
    RowSelectionOptions<TRow, unknown, unknown> | 'single' | 'multiple' | undefined
  >(() => {
    const mode = selection?.mode ?? 'none'
    if (mode === 'singleRow') {
      const opts: RowSelectionOptions<TRow, unknown, unknown> = {
        mode: 'singleRow',
        enableClickSelection: selection?.selectOnClick ?? false,
        checkboxes: !!selection?.checkboxes,
      }
      return opts
    }
    if (mode === 'multiple') {
      const opts: RowSelectionOptions<TRow, unknown, unknown> = {
        mode: 'multiRow',
        // Both of those properties are required for multi-row selection to work
        enableClickSelection: selection?.selectOnClick ?? false,
        enableSelectionWithoutKeys: selection?.selectOnClick ?? false,
        checkboxes: !!selection?.checkboxes,
        selectAll: 'currentPage', // default it "all"
        isRowSelectable: selection?.isRowSelectable,
        hideDisabledCheckboxes: selection?.hideDisabledCheckboxes,
      }
      return opts
    }
    return undefined
  }, [selection])

  // Grid options (sidebar removed in favor of custom column customization)
  const mergedGridOptions = useMemo(
    () =>
      mergeGridOptions<TRow>({
        theme: createAgGridTheme(),
        isExternalFilterPresent,
        doesExternalFilterPass,
        rowSelection,
        sideBar: false,
        noRowsOverlayComponent: AgTableEmptyState,
        suppressNoRowsOverlay: false,
        rowBuffer: 20,
        cacheBlockSize: 100,
        cacheOverflowSize: 2,
        maxConcurrentDatasourceRequests: 2,
        ...(getRowTooltip && {
          tooltipShowDelay: TABLE_TOOLTIP_SHOW_DELAY_MS,
          // Non-interactive tooltips (scope) need a long auto-hide so they remain
          // while the pointer stays on the row. Interactive tooltips (assessment)
          // also need this while hovering the cell; once the pointer is on the
          // tooltip, AG Grid keeps it open via tooltipInteraction.
          tooltipHideDelay: 10_000,
          tooltipMouseTrack: rowTooltipMouseTrack,
          tooltipInteraction: rowTooltipInteractive,
        }),
      }),
    [
      isExternalFilterPresent,
      doesExternalFilterPass,
      rowSelection,
      getRowTooltip,
      rowTooltipInteractive,
      rowTooltipMouseTrack,
    ],
  )

  const syncNoRowsOverlay = useCallback((api: GridApi<TRow>) => {
    if (api.getDisplayedRowCount() === 0) {
      api.showNoRowsOverlay()
      return
    }

    api.hideOverlay()
  }, [])

  const hasActiveSearch = useMemo(() => {
    return searchText.trim().length >= minQueryLength
  }, [searchText, minQueryLength])

  const hasActiveFilters = hasActiveSearch || Object.keys(filterModel).length > 0

  // Applied through the grid rather than the url so the caller does not have to know this table's
  // param key; the existing filter sync then writes it out and the filter bar shows a chip.
  const handleMetricCardClick = useCallback(
    (card: MetricCardItem) => {
      if (!gridApi || !metricCards) return

      gridApi.setFilterModel({
        ...gridApi.getFilterModel(),
        [metricCards.filterColId]: {
          filterType: 'set',
          values: card.filterValues ?? [card.label],
        },
      })
      gridApi.onFilterChanged()
    },
    [gridApi, metricCards],
  )

  const handleClearFilters = useCallback(() => {
    handleSearchChange('')
    setFilterModel({})

    if (!gridApi) {
      return
    }

    gridApi.setFilterModel({})
    gridApi.onFilterChanged()
  }, [gridApi, handleSearchChange])

  const finalGridOptions = useMemo(
    () => ({
      ...mergedGridOptions,
      ...gridOptions,
      onFilterChanged: (params: FilterChangedEvent<TRow>) => {
        setFilterModel((params.api.getFilterModel?.() as FilterModel) || {})
        syncNoRowsOverlay(params.api)
        gridOptions?.onFilterChanged?.(params)
      },
      // onModelUpdated fires for filter/sort/data changes — single source of truth
      // for the displayed content height powering the hug decision.
      onModelUpdated: (params: ModelUpdatedEvent<TRow>) => {
        syncNoRowsOverlay(params.api)
        const total = _.sumBy(
          _.range(params.api.getDisplayedRowCount()),
          (i) => params.api.getDisplayedRowAtIndex(i)?.rowHeight ?? 0,
        )
        setDisplayedContentHeight(total)
        gridOptions?.onModelUpdated?.(params)
      },
    }),
    [mergedGridOptions, gridOptions, syncNoRowsOverlay],
  )

  const effectiveDefaultColDef = useMemo(
    () => ({
      ...getDefaultColDef<TRow>(),
      // Allow column reordering if column customization is enabled
      suppressMovable: !columnCustomization?.enabled,
      tooltipComponent: getRowTooltip ? RowHoverTooltip : undefined,
      tooltipValueGetter: getRowTooltip
        ? (params: ITooltipParams<TRow>) => (params.data ? getRowTooltip(params.data) : undefined)
        : undefined,
    }),
    [columnCustomization?.enabled, getRowTooltip],
  )

  const mergedDefaultColDef = useMemo(
    () => ({ ...effectiveDefaultColDef, ...defaultColDef }),
    [effectiveDefaultColDef, defaultColDef],
  )

  // onGridReady wiring
  const handleGridReady = useCallback(
    (e: GridReadyEvent<TRow>) => {
      onGridInit?.(e)
      if (e.api.getDisplayedRowCount() === 0) {
        e.api.showNoRowsOverlay()
      }
      setFilterModel((e.api.getFilterModel?.() as FilterModel) || {})
      setGridApi(e.api)
      onGridReady?.(e.api)
    },
    [onGridInit, onGridReady],
  )

  useEffect(() => {
    const element = metricCardsRef.current
    if (!element) {
      setMetricCardsHeight(0)
      return undefined
    }

    const observer = new ResizeObserver(() => setMetricCardsHeight(element.offsetHeight))
    observer.observe(element)
    return () => observer.disconnect()
  }, [metricCards])

  const handleColumnStateReset = useCallback(() => {
    clearStoredColumnState()
    if (prefixedParamKeys.columns) {
      replaceParams({
        remove: [prefixedParamKeys.columns],
        replaceCurrentHistoryEntry: true,
      })
    }
  }, [clearStoredColumnState, prefixedParamKeys.columns, replaceParams])

  // Listen for column state changes from the grid (drag-and-drop, manual column moves)
  // and sync them to localStorage
  useEffect(() => {
    if (!gridApi || !columnCustomization?.enabled) {
      return undefined
    }

    const handleColumnStateChange = () => {
      const columnState = gridApi.getColumnState()
      setStoredColumnState(columnState)
    }

    // Listen to various column events
    gridApi.addEventListener('columnMoved', handleColumnStateChange)
    gridApi.addEventListener('columnVisible', handleColumnStateChange)
    gridApi.addEventListener('columnPinned', handleColumnStateChange)

    return () => {
      gridApi.removeEventListener('columnMoved', handleColumnStateChange)
      gridApi.removeEventListener('columnVisible', handleColumnStateChange)
      gridApi.removeEventListener('columnPinned', handleColumnStateChange)
    }
  }, [gridApi, columnCustomization?.enabled, setStoredColumnState])

  // One-time view apply when grid becomes ready
  const initialViewAppliedRef = useRef(false)
  useEffect(() => {
    if (!gridApi || !views?.options?.length || initialViewAppliedRef.current) return
    if (!currentView) return
    initialViewAppliedRef.current = true
    // If URL already contains filter params, still apply column state but preserve filters
    // Use the same prefixed keys that were passed to useAgViewManager
    const hasFilterParams =
      !preventFilters && !!prefixedParamKeys.filter && !!definedUrlParams[prefixedParamKeys.filter]
    applyView(currentView, hasFilterParams /* preserveExistingFilters */, false /* clearUrl */)
  }, [
    gridApi,
    currentView,
    applyView,
    views?.options,
    definedUrlParams,
    prefixedParamKeys.filter,
    preventFilters,
  ])

  const providerValue = useMemo(
    () => ({
      gridApi,
      columnDefs: columnDefsWithStoredState,
      originalColumnDefs: columnDefs, // Original column defs before applying stored state
      currentViewName: currentView,
      getViewDefinition: views?.getDefinition
        ? (name: string) => {
            const def = views.getDefinition(name)
            return {
              label: def?.label,
              filterModel: (def?.filterModel || {}) as FilterModel,
              columnState: def?.columnState,
              filterBarFilters: def?.filterBarFilters,
            }
          }
        : undefined,
      searchValue: searchText,
      onSearchChange: handleSearchChange,
      enableCsv: exportCsv?.enabled !== false,
      defaultVisibleFilters: filters?.defaultVisibleFilters,
      removableFilters: filters?.removableFilters,
      onColumnStateReset: columnCustomization?.enabled ? handleColumnStateReset : undefined,
      // Empty state customization
      entityName,
      filterModel,
      hasActiveFilters,
      onClearFilters: handleClearFilters,
      headerEmptyStateMessage,
      subHeaderEmptyStateMessage,
      emptyStateButton,
      emptyStateDefinition,
    }),
    [
      gridApi,
      columnDefsWithStoredState,
      columnDefs,
      currentView,
      views,
      searchText,
      handleSearchChange,
      exportCsv?.enabled,
      filters?.defaultVisibleFilters,
      filters?.removableFilters,
      columnCustomization?.enabled,
      handleColumnStateReset,
      entityName,
      filterModel,
      hasActiveFilters,
      handleClearFilters,
      headerEmptyStateMessage,
      subHeaderEmptyStateMessage,
      emptyStateButton,
      emptyStateDefinition,
    ],
  )

  return (
    <AgGridProvider<TRow> value={providerValue}>
      <div
        data-table-mode={tableMode}
        className={cn(
          {
            // Fill: bound to parent with overflow control. Hug/print: size to content.
            'flex h-full flex-col overflow-hidden': tableMode === 'full',
            'flex flex-col': tableMode !== 'full',
          },
          className,
        )}
      >
        {tableMode !== 'print' && !hideHeader && (
          <div className='shrink-0'>
            {!hideViewsSwitcher && views?.options?.length ? (
              <div className='mb-4'>
                <AgViewsSwitcher
                  views={viewItems}
                  selectedView={currentView || views?.defaultView || views.options[0]}
                  onViewChange={(name) => {
                    const def = views?.defaultView || 'All'
                    if (name === def) applyView(name, false, true)
                    else applyView(name)
                  }}
                />
              </div>
            ) : null}

            {metricCards?.cards.length ? (
              // Padding rather than a margin so the measured box already includes the gap below.
              <div ref={metricCardsRef} className='pb-4'>
                <AgMetricCards cards={metricCards.cards} onCardClick={handleMetricCardClick} />
              </div>
            ) : null}

            <div className={cn('mb-4', toolbarClassName)}>
              <AgFilterBar<TRow>
                hideFilters={hideFilters}
                gridApi={gridApi}
                columnDefs={columnDefsWithStoredState}
                quickFilterText={searchText}
                onSearch={handleSearchChange}
                currentViewName={currentView}
                getViewDefinition={views ? (name) => views.getDefinition(name) : undefined}
                hideSearch={!!hideSearch}
                hideExport={!!hideExport}
                hideCount={!!hideCount}
                searchContainerClassName={search?.containerClassName}
                columnCustomizationEnabled={columnCustomization?.enabled}
                agCsvExportConfig={agCsvExportConfig}
                customLeftContent={customFilterBarLeft}
                chipStorage={chipStorage}
                trackingContext={trackingContext}
                inModal={inModal}
                grouping={grouping}
                searchPlacement={searchPlacement}
              />
            </div>

            {(headerLeft || headerRight) && (
              <div className='mb-4 flex items-center justify-between'>
                <div>{headerLeft}</div>
                <div>{headerRight}</div>
              </div>
            )}
          </div>
        )}

        <div className={cn({ 'min-h-0 flex-1': tableMode === 'full' })}>
          {containerClassName ? (
            <AgDataTableWrapper className={tableMode === 'hug' ? '' : containerClassName}>
              <AgTable<TRow>
                rowData={rowData}
                columnDefs={columnDefsWithStoredState}
                defaultColDef={mergedDefaultColDef}
                gridOptions={finalGridOptions}
                onGridReady={handleGridReady}
                onRowClick={onRowClick}
                searchContext={{ query: searchText, results: search?.results ?? [] }}
                className={cn(
                  {
                    'size-full': tableMode === 'full',
                    '!min-h-0': tableMode === 'hug',
                  },
                  tableClassName,
                )}
                withFillerColumn={withFillerColumn}
                borderless={borderless}
                domLayout={tableMode === 'hug' ? 'autoHeight' : domLayout}
                emptyStateContent={emptyStateContent}
                onFilterChanged={onFilterChanged}
              />
            </AgDataTableWrapper>
          ) : (
            <AgTable<TRow>
              rowData={rowData}
              columnDefs={columnDefsWithStoredState}
              defaultColDef={mergedDefaultColDef}
              gridOptions={finalGridOptions}
              onGridReady={handleGridReady}
              onRowClick={onRowClick}
              searchContext={{ query: searchText, results: search?.results ?? [] }}
              className={cn(
                {
                  'size-full': tableMode === 'full',
                  '!min-h-0': tableMode === 'hug',
                },
                tableClassName,
              )}
              withFillerColumn={withFillerColumn}
              borderless={borderless}
              domLayout={tableMode === 'hug' ? 'autoHeight' : domLayout}
              emptyStateContent={emptyStateContent}
              onFilterChanged={onFilterChanged}
            />
          )}
        </div>

        {tableMode !== 'print' && footer}
      </div>
    </AgGridProvider>
  )
}

AgDataTable.displayName = 'AgDataTable'
